import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  EditIcon,
  EmptyState,
  InfoIcon,
  LockIcon,
  Tooltip,
  UnlockIcon,
} from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import {
  use_SchoolYearsQuery,
  useDeleteTeachingBlockMutation,
  useDeleteTeachingBlockVersionMutation,
  useDuplicateTeachingBlockVersionMutation,
  useSetTeachingBlockVersionEditableMutation,
  useUpdateTeachingBlockVersionMutation,
} from '../../../types/planung-graphql-client-defs';
import { blockIfEventJobRunning, createEventsForDateRange } from '../../../utils/eventsApi';
import {
  showSuccessDeleteToast,
  showSuccessDuplicateToast,
  showSuccessSaveToast,
  showUserErrorToast,
} from '../../../utils/toast';
import { useTeachingBlockForSubjectContainer } from '../../Teachingblock/hooks/useTeachingBlocks';
import { TeachingBlockVersionModalForm } from '../Form/TeachingBlockVersionModalForm';
import styles from './TeachingBlockVersionsOverview.module.scss';

export type TeachingBlockVersion = {
  uuid: string;
  active: boolean;
  version: number;
  updatedAt: string;
  description: string;
  isSnapshot: boolean;
  editable: boolean;
  originalUuid: string | null;
};

type TeachingBlockVersionsOverviewProps = {
  schoolYearUuid: string;
  onNavigate: () => void;
  subjectContainerUuid: string;
};

export const TeachingBlockVersionsOverview: FC<TeachingBlockVersionsOverviewProps> = ({
  schoolYearUuid,
  onNavigate,
  subjectContainerUuid,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const permissions = usePermissionChecker();

  const { confirm, ConfirmationDialog } = useConfirm();

  const [, updateVersion] = useUpdateTeachingBlockVersionMutation();
  const [, setVersionEditable] = useSetTeachingBlockVersionEditableMutation();

  const [, deleteTeachingBlockVersion] = useDeleteTeachingBlockVersionMutation();

  const [, duplicateVersion] = useDuplicateTeachingBlockVersionMutation();
  const [, deleteTeachingBlock] = useDeleteTeachingBlockMutation();

  const [loading, setLoading] = useState<string | null>(null);
  const [versionToEdit, setVersionToEdit] = useState<TeachingBlockVersion | null>(null);

  const context = useMemo(() => {
    return {
      additionalTypenames: [
        'TeachingBlockVersion',
        'TeachingBlock',
        'SubjectContainer',
        'TeachingBlockCard',
        'LessonClass',
      ],
    };
  }, []);

  const { teachingBlock } = useTeachingBlockForSubjectContainer(schoolYearUuid, subjectContainerUuid);

  const memoizedData: TeachingBlockVersion[] = useMemo(() => {
    const versions = [...(teachingBlock?.versions ?? [])];

    versions.sort((a, b) => {
      const aDate = a.updatedAt ? dayjs(a.updatedAt) : dayjs(a.createdAt);
      const bDate = b.updatedAt ? dayjs(b.updatedAt) : dayjs(b.createdAt);
      return aDate.isSameOrBefore(bDate) ? 1 : -1;
    });

    return versions.map((version) => {
      return {
        uuid: version.uuid,
        active: version.active ?? false,
        version: version.version,
        isSnapshot: version.isSnapshot ?? false,
        originalUuid: version.originalUuid ?? null,
        editable: version.editable ?? false,
        updatedAt: version.updatedAt
          ? dayjs(version.updatedAt).format('DD.MM.YYYY - HH:mm')
          : dayjs(version.createdAt).format('DD.MM.YYYY - HH:mm'),
        description: version.description ?? '',
      };
    });
  }, [teachingBlock]);

  const activeVersion = memoizedData.find((v) => v.active);

  const otherVersions = memoizedData.filter((t) => !t.isSnapshot).filter((t) => !t.active);

  const snapshotVersions = memoizedData.filter((t) => t.isSnapshot);

  const [{ data: schoolYearData }] = use_SchoolYearsQuery({});
  const schoolYear = schoolYearData?.schoolYears.find((sy) => sy.uuid === schoolYearUuid);

  // "Gestartet" analog zu GridRows: das Schuljahr (= Zeitraum der Epoche) hat
  // begonnen. Erst dann ist eine aktive Version schützenswert und das
  // Sperren/Entsperren (editable) relevant.
  const hasStarted = schoolYear ? dayjs().isSameOrAfter(dayjs(schoolYear.start), 'day') : false;
  // Termine der Epoche liegen frühestens am Schuljahresbeginn — ein Zeitraum ab heute würde bei einer
  // Epoche fürs nächste Schuljahr den Rest des laufenden Schuljahres mit erfassen.
  const eventsFrom = schoolYear && !hasStarted ? dayjs(schoolYear.start) : dayjs();
  const editable = activeVersion?.editable ?? true;

  function onDataNavigate(uuid: string) {
    onNavigate();
    navigate(
      `${t('routes.teachingBlockVersions.slug')}/${uuid}/${t('routes.versionPages.data.slug')}/${t('routes.versionsFilter.class.slug')}`,
    );
  }

  function onBoardNavigate(uuid: string) {
    onNavigate();
    navigate(`${t('routes.teachingBlockVersions.slug')}/${uuid}/${t('routes.versionPages.board.slug')}`);
  }

  async function onSave(editVersion: Partial<TeachingBlockVersion>) {
    if (!editVersion || !editVersion.uuid) return;
    if (await blockIfEventJobRunning()) return;
    setLoading(editVersion.uuid);

    const previouslyActive = memoizedData.find((v) => v.uuid === editVersion.uuid)?.active ?? false;
    const activeStatusChangingToTrue = editVersion.active === true && !previouslyActive;

    // Beim Aktivieren deaktiviert das Backend die bisher aktive Version im selben Schritt.
    const result = await updateVersion(
      // ohne Angabe bleibt der Aktiv-Status, wie er ist
      {
        uuid: editVersion.uuid,
        input: { active: editVersion.active ?? previouslyActive, description: editVersion.description },
      },
      context,
    );
    if (!result || result.error) {
      showUserErrorToast({ text: t('common.errorToastText'), error: result.error });
    } else {
      showSuccessSaveToast();
      if (activeStatusChangingToTrue && teachingBlock?.draft === false && schoolYear) {
        await createEventsForDateRange({
          organizationUuid: pimAuthClaims.getOrganizationUuid(),
          schoolYearUuid,
          from: eventsFrom.format('YYYY-MM-DD'),
          to: dayjs(schoolYear.end).format('YYYY-MM-DD'),
          epochSubjectContainerUuid: subjectContainerUuid,
        });
      }
    }
    setLoading(null);
    setVersionToEdit(null);
  }

  async function onDelete(version: TeachingBlockVersion) {
    const isLastVersion = memoizedData.length === 1;
    const confirmed = await confirm({ title: t('common.delete'), message: t('teachingBlockVersion.deleteMessage') });
    if (!confirmed) return;
    setLoading(version.uuid);

    // Eine Mutation löscht Karten, Ausschlüsse, die nur dort angelegten Zuordnungen und die Version.
    const { error } = await deleteTeachingBlockVersion({ uuid: version.uuid }, context);

    if (error) {
      showUserErrorToast({ error });
    } else {
      showSuccessDeleteToast();
    }
    setLoading(null);
    if (isLastVersion) {
      // Das Backend löscht nur einen Epochenplan ohne Versionen.
      if (teachingBlock) {
        await deleteTeachingBlock({ uuid: teachingBlock.uuid }, context);
      }
      handleClose();
    }
  }

  function handleClose() {
    setLoading(null);
    setVersionToEdit(null);
  }

  const onDuplicate = async (version: TeachingBlockVersion) => {
    setLoading(version.uuid);
    const result = await duplicateVersion(
      {
        teachingBlockVersionUuid: version.uuid,
        options: {},
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
      },
      context,
    );

    if (!result || result.error) {
      showUserErrorToast({ error: result.error });
    } else {
      showSuccessDuplicateToast();
    }
    setLoading(null);
  };

  // Korrektur abschliessen: aktive Version wieder sperren (editable = false).
  const lockVersion = async (version: TeachingBlockVersion) => {
    if (await blockIfEventJobRunning()) return;
    setLoading(version.uuid);
    const result = await setVersionEditable({ uuid: version.uuid, editable: false }, context);
    if (!result || result.error) {
      showUserErrorToast({ error: result.error });
    } else {
      showSuccessSaveToast();
      if (teachingBlock?.draft === false && schoolYear) {
        await createEventsForDateRange({
          organizationUuid: pimAuthClaims.getOrganizationUuid(),
          schoolYearUuid,
          from: eventsFrom.format('YYYY-MM-DD'),
          to: dayjs(schoolYear.end).format('YYYY-MM-DD'),
          epochSubjectContainerUuid: subjectContainerUuid,
        });
      }
    }
    setLoading(null);
  };

  // Version korrigieren: gesperrte aktive Version entsperren (editable = true) und
  // zuvor einen Snapshot als Sicherung anlegen.
  const fixVersion = async (version: TeachingBlockVersion) => {
    if (await blockIfEventJobRunning()) return;
    const res = await confirm({
      title: t('timetableVersion.attention'),
      message: t('timetableVersion.fixVersionMessage'),
      confirmText: t('timetableVersion.fixVersion'),
      cancelText: t('common.cancel'),
    });
    if (!res) return;
    setLoading(version.uuid);

    await setVersionEditable({ uuid: version.uuid, editable: true }, context);

    const { data, error } = await duplicateVersion(
      {
        teachingBlockVersionUuid: version.uuid,
        options: { snapshot: true },
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
      },
      context,
    );
    setLoading(null);
    if (!data || error) {
      showUserErrorToast({ error });
    } else {
      onDataNavigate(version.uuid);
    }
  };

  return (
    <div className={styles['teaching-block-versions-overview']}>
      {editable && hasStarted && activeVersion && (
        <div className={styles['editable-hint']}>
          <InfoIcon color='var(--color-warning)' />
          {t('timetableVersion.editableVersionHint')}
        </div>
      )}
      <div className={styles.active}>
        <div className={styles.hint}>{t('teachingBlockVersion.activeVersion')}</div>
        <div className={styles.list}>
          <div className={styles.header}>
            <div className={styles.version}>{t('teachingBlockVersion.title', { count: 1 })}</div>
            <div className={styles.edited}>{t('common.updatedAt')}</div>
            <div className={styles.name}>{t('common.name')}</div>
            <div className={styles.status}>{t('common.status')}</div>
            <div className={styles.actions}></div>
          </div>
          {activeVersion ? (
            <div className={styles.data}>
              <div className={styles.version}>{activeVersion.version}</div>
              <div className={styles.edited}>{activeVersion.updatedAt}</div>
              <div className={styles.name}>{activeVersion.description}</div>
              <div className={styles.status}>
                {hasStarted &&
                  (editable ? (
                    <Tooltip triggerClass={styles.lock} content={t('teachingBlockVersion.lockedEditable')}>
                      <UnlockIcon />
                    </Tooltip>
                  ) : (
                    <Tooltip triggerClass={styles.lock} content={t('teachingBlockVersion.locked')}>
                      <LockIcon />
                    </Tooltip>
                  ))}
              </div>
              <div className={styles.actions}>
                <Button hierarchy='tertiary' onClick={() => onDataNavigate(activeVersion.uuid)}>
                  {t('teachingBlockVersion.teachingBlockData')}
                </Button>
                <Button hierarchy='tertiary' className='mr-3' onClick={() => onBoardNavigate(activeVersion.uuid)}>
                  {t('pinboard.title', { count: 1 })}
                </Button>
                <ButtonGroup
                  loading={!!loading && loading === activeVersion.uuid}
                  disabled={!permissions?.canWriteTimetable()}
                >
                  <Button
                    icon={<EditIcon className='small' />}
                    hierarchy='tertiary'
                    onClick={() => setVersionToEdit(activeVersion)}
                    disabled={!permissions?.canWriteTimetable()}
                  />
                  <Dropdown
                    usePortal={false}
                    disabled={!permissions?.canWriteTimetable()}
                    trigger={
                      <Button
                        disabled={!permissions?.canWriteTimetable()}
                        icon={<DotsHorizontalIcon className='small' />}
                        hierarchy='tertiary'
                      />
                    }
                  >
                    <DropdownMenu
                      data={[
                        {
                          label: t('common.deactivate'),
                          onClick: () => onSave({ ...activeVersion, active: false }),
                        },
                        {
                          label: t('common.duplicate'),
                          onClick: () => onDuplicate(activeVersion),
                        },
                        { type: 'ruler' },
                        {
                          label: t('timetableVersion.snapshots'),
                          disabled: snapshotVersions.length === 0,
                          subContent: [
                            ...snapshotVersions
                              .filter((v) => v.originalUuid === activeVersion.uuid)
                              .map((version) => ({
                                label: version.updatedAt,
                                onClick: () => onDataNavigate(version.uuid),
                              })),
                          ],
                        },
                        ...(hasStarted
                          ? [
                              {
                                type: 'default' as const,
                                color: 'error' as const,
                                label: editable ? t('timetableVersion.completeFix') : t('timetableVersion.fixVersion'),
                                onClick: () => (editable ? lockVersion(activeVersion) : fixVersion(activeVersion)),
                              },
                            ]
                          : []),
                        {
                          type: 'ruler',
                        },
                        {
                          label: t('common.delete'),
                          type: 'default',
                          color: 'error',
                          disabled: true,
                          onClick: () => onDelete(activeVersion),
                        },
                      ]}
                    />
                  </Dropdown>
                </ButtonGroup>
              </div>
            </div>
          ) : (
            <EmptyState
              title={t('teachingBlockVersion.noActiveVersion')}
              subtitle={t('teachingBlockVersion.noActiveVersionHint')}
              size='small'
              forcedHeight='100px'
              hideIcon
            />
          )}
        </div>
      </div>
      <div className={styles.versions}>
        <div className={styles.hint}>{t('teachingBlockVersion.pastVersions')}</div>
        <div className={styles.list}>
          <div className={styles.header}>
            <div className={styles.version}>{t('teachingBlockVersion.title', { count: 1 })}</div>
            <div className={styles.edited}>{t('common.updatedAt')}</div>
            <div className={styles.name}>{t('common.name')}</div>
            <div className={styles.status}>{t('common.status')}</div>
            <div className={styles.actions}></div>
          </div>
          {otherVersions.length === 0 ? (
            <EmptyState
              title={t('teachingBlockVersion.noPastVersions')}
              subtitle={t('teachingBlockVersion.noPastVersionsHint')}
              size='small'
              forcedHeight='100px'
              hideIcon
            />
          ) : (
            <div className={classNames(styles['data-wrapper'], 'has-scrollbar')}>
              {otherVersions.map((version) => {
                return (
                  <div className={styles.data} key={version.uuid}>
                    <div className={styles.version}>{version.version}</div>
                    <div className={styles.edited}>{version.updatedAt}</div>
                    <div className={styles.name}>{version.description}</div>
                    {/*<div className={styles.status}>{`${version.status}%`}</div>*/}
                    <div className={styles.actions}>
                      <Button hierarchy='tertiary' onClick={() => onDataNavigate(version.uuid)}>
                        {t('teachingBlockVersion.teachingBlockData')}
                      </Button>
                      <Button hierarchy='tertiary' className='mr-3' onClick={() => onBoardNavigate(version.uuid)}>
                        {t('pinboard.title', { count: 1 })}
                      </Button>
                      <ButtonGroup disabled={!permissions?.canWriteTimetable()}>
                        <Button
                          icon={<EditIcon className='small' />}
                          hierarchy='tertiary'
                          onClick={() => setVersionToEdit(version)}
                        />
                        <Dropdown
                          usePortal={false}
                          trigger={<Button icon={<DotsHorizontalIcon className='small' />} hierarchy='tertiary' />}
                        >
                          <DropdownMenu
                            data={[
                              {
                                label: t('common.activate'),
                                onClick: () => onSave({ ...version, active: true }),
                              },
                              {
                                label: t('common.duplicate'),
                                onClick: () => onDuplicate(version),
                              },
                              {
                                type: 'ruler',
                              },
                              {
                                label: t('common.delete'),
                                type: 'default',
                                color: 'error',
                                onClick: () => onDelete(version),
                              },
                            ]}
                          />
                        </Dropdown>
                      </ButtonGroup>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <TeachingBlockVersionModalForm
        editVersion={versionToEdit}
        loading={!!loading}
        onSave={onSave}
        onClose={handleClose}
      />
      <ConfirmationDialog />
    </div>
  );
};
